import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Assignment,
  Schedule,
  DirectionsCar,
  Person,
  Warning,
  CheckCircle,
  Error,
  Download,
  Refresh,
  Visibility,
  ExpandMore,
  TrendingUp,
  TrendingDown,
  Assessment
} from '@mui/icons-material';
import apiClient from '../../api';

const ComplianceReports = () => {
  const [hosLogs, setHosLogs] = useState([]);
  const [hosSummary, setHosSummary] = useState(null);
  const [dvirReports, setDvirReports] = useState([]);
  const [complianceReports, setComplianceReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30'); // days
  const [activeTab, setActiveTab] = useState(0);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverHosDetails, setDriverHosDetails] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    loadComplianceData();
  }, [dateRange]);

  const loadComplianceData = async () => {
    try {
      setLoading(true);
      setError(null);

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const startTime = startDate.toISOString();
      const endTime = endDate.toISOString();

      // Load all compliance data in parallel
      const [hosLogsResponse, hosSummaryResponse, dvirResponse, complianceResponse] = await Promise.allSettled([
        apiClient.get(`/samsara/compliance/hos-logs?startTime=${startTime}&endTime=${endTime}`),
        apiClient.get(`/samsara/compliance/hos-summary?startTime=${startTime}&endTime=${endTime}`),
        apiClient.get(`/samsara/compliance/dvirs?startTime=${startTime}&endTime=${endTime}`),
        apiClient.get(`/samsara/compliance/reports?startTime=${startTime}&endTime=${endTime}`)
      ]);

      if (hosLogsResponse.status === 'fulfilled') {
        setHosLogs(hosLogsResponse.value.data.data || []);
      } else {
        console.warn('Failed to load HOS logs:', hosLogsResponse.reason);
        setHosLogs([]);
      }

      if (hosSummaryResponse.status === 'fulfilled') {
        setHosSummary(hosSummaryResponse.value.data);
      } else {
        console.warn('Failed to load HOS summary:', hosSummaryResponse.reason);
      }

      if (dvirResponse.status === 'fulfilled') {
        setDvirReports(dvirResponse.value.data.data || []);
      } else {
        console.warn('Failed to load DVIR reports:', dvirResponse.reason);
        setDvirReports([]);
      }

      if (complianceResponse.status === 'fulfilled') {
        setComplianceReports(complianceResponse.value.data);
      } else {
        console.warn('Failed to load compliance reports:', complianceResponse.reason);
      }
    } catch (err) {
      console.error('Error loading compliance data:', err);
      setError('Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadComplianceData();
    setRefreshing(false);
  };

  const handleViewDriverHos = async (driver) => {
    setSelectedDriver(driver);
    setDetailsOpen(true);
    
    try {
      const response = await apiClient.get(`/samsara/drivers/${driver.id}/stats?startTime=${getDateRange().start}&endTime=${getDateRange().end}`);
      setDriverHosDetails(response.data);
    } catch (err) {
      console.error('Error loading driver HOS details:', err);
    }
  };

  const getDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - parseInt(dateRange));
    return {
      start: start.toISOString(),
      end: end.toISOString()
    };
  };

  const getComplianceStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'compliant':
      case 'ok':
        return 'success';
      case 'warning':
      case 'caution':
        return 'warning';
      case 'violation':
      case 'non_compliant':
        return 'error';
      default:
        return 'default';
    }
  };

  const getComplianceStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'compliant':
      case 'ok':
        return <CheckCircle color="success" />;
      case 'warning':
      case 'caution':
        return <Warning color="warning" />;
      case 'violation':
      case 'non_compliant':
        return <Error color="error" />;
      default:
        return <Assessment />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return Number(num).toLocaleString();
  };

  const getDriverHosSummary = () => {
    const driverStats = {};
    hosLogs.forEach(log => {
      const driverId = log.driverId || log.driver?.id;
      if (driverId) {
        if (!driverStats[driverId]) {
          driverStats[driverId] = {
            driverId,
            driverName: log.driver?.name || log.driver?.username || `Driver ${driverId}`,
            totalHours: 0,
            violations: 0,
            logs: []
          };
        }
        driverStats[driverId].totalHours += log.duration || 0;
        if (log.violation) driverStats[driverId].violations++;
        driverStats[driverId].logs.push(log);
      }
    });
    return Object.values(driverStats);
  };

  const getDvirSummary = () => {
    const vehicleStats = {};
    dvirReports.forEach(dvir => {
      const vehicleId = dvir.vehicleId || dvir.vehicle?.id;
      if (vehicleId) {
        if (!vehicleStats[vehicleId]) {
          vehicleStats[vehicleId] = {
            vehicleId,
            vehicleName: dvir.vehicle?.name || `Vehicle ${vehicleId}`,
            totalReports: 0,
            defects: 0,
            reports: []
          };
        }
        vehicleStats[vehicleId].totalReports++;
        if (dvir.defects && dvir.defects.length > 0) {
          vehicleStats[vehicleId].defects += dvir.defects.length;
        }
        vehicleStats[vehicleId].reports.push(dvir);
      }
    });
    return Object.values(vehicleStats);
  };

  const driverHosSummary = getDriverHosSummary();
  const dvirSummary = getDvirSummary();

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
              Compliance Reports
            </Typography>
            <Box display="flex" gap={1}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Date Range"
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <MenuItem value="7">Last 7 days</MenuItem>
                  <MenuItem value="30">Last 30 days</MenuItem>
                  <MenuItem value="90">Last 90 days</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title="Refresh Data">
                <IconButton onClick={handleRefresh} disabled={refreshing}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Compliance Overview */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Schedule color="primary" />
                    <Typography variant="h6">
                      {hosLogs.length}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    HOS Logs
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <DirectionsCar color="primary" />
                    <Typography variant="h6">
                      {dvirReports.length}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    DVIR Reports
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Warning color="warning" />
                    <Typography variant="h6">
                      {driverHosSummary.filter(d => d.violations > 0).length}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    HOS Violations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Error color="error" />
                    <Typography variant="h6">
                      {dvirSummary.reduce((sum, v) => sum + v.defects, 0)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Defects
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Compliance Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="HOS Logs" />
            <Tab label="DVIR Reports" />
            <Tab label="Compliance Summary" />
          </Tabs>
        </Box>

        {/* HOS Logs Tab */}
        {activeTab === 0 && (
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Hours of Service (HOS) Logs
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Driver</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Violations</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hosLogs.map((log, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person fontSize="small" />
                          <Box>
                            <Typography variant="subtitle2">
                              {log.driver?.name || log.driver?.username || `Driver ${log.driverId}`}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              ID: {log.driverId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getComplianceStatusIcon(log.status)}
                          <Chip
                            label={log.status || 'Unknown'}
                            color={getComplianceStatusColor(log.status)}
                            size="small"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDuration(log.duration)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(log.startTime)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(log.endTime)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.violations && log.violations.length > 0 ? (
                          <Chip
                            label={`${log.violations.length} violations`}
                            color="error"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="Compliant"
                            color="success"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDriverHos(log.driver || { id: log.driverId })}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {hosLogs.length === 0 && (
              <Box textAlign="center" py={4}>
                <Alert severity="info">
                  No HOS logs available. Configure Samsara integration to view HOS compliance data.
                </Alert>
              </Box>
            )}
          </CardContent>
        )}

        {/* DVIR Reports Tab */}
        {activeTab === 1 && (
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Driver Vehicle Inspection Reports (DVIR)
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Inspection Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Defects</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dvirReports.map((dvir, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <DirectionsCar fontSize="small" />
                          <Box>
                            <Typography variant="subtitle2">
                              {dvir.vehicle?.name || `Vehicle ${dvir.vehicleId}`}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              ID: {dvir.vehicleId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person fontSize="small" />
                          <Typography variant="body2">
                            {dvir.driver?.name || dvir.driver?.username || `Driver ${dvir.driverId}`}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(dvir.inspectionDate || dvir.timestamp)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={dvir.status || 'Unknown'}
                          color={dvir.defects && dvir.defects.length > 0 ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {dvir.defects && dvir.defects.length > 0 ? (
                          <Chip
                            label={`${dvir.defects.length} defects`}
                            color="error"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="No defects"
                            color="success"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {dvirReports.length === 0 && (
              <Box textAlign="center" py={4}>
                <Alert severity="info">
                  No DVIR reports available. Configure Samsara integration to view vehicle inspection data.
                </Alert>
              </Box>
            )}
          </CardContent>
        )}

        {/* Compliance Summary Tab */}
        {activeTab === 2 && (
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Compliance Summary
            </Typography>
            
            {/* HOS Summary */}
            {hosSummary && (
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1">HOS Compliance Summary</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Total Hours Logged: {formatDuration(hosSummary.totalHours)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Violations: {formatNumber(hosSummary.violations)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Compliance Rate: {formatNumber(hosSummary.complianceRate)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Last Updated: {formatDate(hosSummary.lastUpdated)}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Driver HOS Summary */}
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">Driver HOS Summary</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {driverHosSummary.map((driver) => (
                    <ListItem key={driver.driverId} divider>
                      <ListItemIcon>
                        <Person />
                      </ListItemIcon>
                      <ListItemText
                        primary={driver.driverName}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              Total Hours: {formatDuration(driver.totalHours)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Violations: {driver.violations}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip
                        label={driver.violations > 0 ? `${driver.violations} violations` : 'Compliant'}
                        color={driver.violations > 0 ? 'error' : 'success'}
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* DVIR Summary */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">Vehicle Inspection Summary</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {dvirSummary.map((vehicle) => (
                    <ListItem key={vehicle.vehicleId} divider>
                      <ListItemIcon>
                        <DirectionsCar />
                      </ListItemIcon>
                      <ListItemText
                        primary={vehicle.vehicleName}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              Total Reports: {vehicle.totalReports}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Defects Found: {vehicle.defects}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip
                        label={vehicle.defects > 0 ? `${vehicle.defects} defects` : 'No defects'}
                        color={vehicle.defects > 0 ? 'error' : 'success'}
                        size="small"
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        )}
      </Card>

      {/* Driver HOS Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              HOS Details: {selectedDriver?.name || selectedDriver?.username || `Driver ${selectedDriver?.id}`}
            </Typography>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDriver && driverHosDetails && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Hours of Service Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Total Drive Time: {formatDuration(driverHosDetails.totalDriveTime)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Total On-Duty Time: {formatDuration(driverHosDetails.totalOnDutyTime)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Remaining Hours: {formatDuration(driverHosDetails.remainingHours)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    Status: 
                    <Chip
                      label={driverHosDetails.status || 'Unknown'}
                      color={getComplianceStatusColor(driverHosDetails.status)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ComplianceReports;
